/**
 * Sets or removes the aria-expanded attribute on an element
 * @param element - The HTML element to update
 * @param expanded - Whether the element is expanded (true/false) or null to remove the attribute
 */
export function setAriaExpanded(element: HTMLElement | null, expanded: boolean | null): void {
    if (!element) return;
    
    if (expanded === null) {
        element.removeAttribute('aria-expanded');
    } else {
        element.setAttribute('aria-expanded', String(expanded));
    }
}

/**
 * Sets or removes the aria-hidden attribute on an element
 * @param element - The HTML element to update
 * @param hidden - Whether the element is hidden (true/false) or null to remove the attribute
 */
export function setAriaHidden(element: HTMLElement | null, hidden: boolean | null): void {
    if (!element) return;
    
    if (hidden === null) {
        element.removeAttribute('aria-hidden');
    } else {
        element.setAttribute('aria-hidden', String(hidden));
    }
}

/**
 * Sets or removes the aria-selected attribute on an element
 * @param element - The HTML element to update
 * @param selected - Whether the element is selected (true/false) or null to remove the attribute
 */
export function setAriaSelected(element: HTMLElement | null, selected: boolean | null): void {
    if (!element) return;
    
    if (selected === null) {
        element.removeAttribute('aria-selected');
    } else {
        element.setAttribute('aria-selected', String(selected));
    }
}

/**
 * Sets or removes the aria-label attribute on an element
 * @param element - The HTML element to update
 * @param label - The label text or null to remove the attribute
 */
export function setAriaLabel(element: HTMLElement | null, label: string | null): void {
    if (!element) return;
    
    if (label === null) {
        element.removeAttribute('aria-label');
    } else {
        element.setAttribute('aria-label', label);
    }
}

/**
 * Manages multiple aria attributes on an element in a single call
 * @param element - The HTML element to update
 * @param attributes - Object containing aria attributes to set
 */
export function setAriaAttributes(element: HTMLElement | null, attributes: {
    expanded?: boolean | null;
    hidden?: boolean | null;
    selected?: boolean | null;
    label?: string | null;
}): void {
    if (!element) return;
    
    if (attributes.expanded !== undefined) {
        setAriaExpanded(element, attributes.expanded);
    }
    
    if (attributes.hidden !== undefined) {
        setAriaHidden(element, attributes.hidden);
    }
    
    if (attributes.selected !== undefined) {
        setAriaSelected(element, attributes.selected);
    }
    
    if (attributes.label !== undefined) {
        setAriaLabel(element, attributes.label);
    }
}

/**
 * Gets the current aria-expanded state of an element
 * @param element - The HTML element to check
 * @returns true if expanded, false if collapsed, null if attribute doesn't exist
 */
export function getAriaExpanded(element: HTMLElement | null): boolean | null {
    if (!element) return null;
    
    const value = element.getAttribute('aria-expanded');
    if (value === null) return null;
    return value === 'true';
}

/**
 * Toggles the aria-expanded state of an element
 * @param element - The HTML element to toggle
 * @returns The new expanded state
 */
export function toggleAriaExpanded(element: HTMLElement | null): boolean | null {
    if (!element) return null;
    
    const currentState = getAriaExpanded(element);
    const newState = currentState === null ? false : !currentState;
    setAriaExpanded(element, newState);
    return newState;
}

/**
 * Sets up standard accessibility attributes for interactive elements
 * @param element - The HTML element to configure
 * @param options - Configuration options
 */
export function setupInteractiveElement(element: HTMLElement | null, options: {
    role?: string;
    tabindex?: string | number;
    ariaExpanded?: boolean;
    ariaLabel?: string;
}): void {
    if (!element) return;
    
    if (options.role) {
        element.setAttribute('role', options.role);
    }
    
    if (options.tabindex !== undefined) {
        element.setAttribute('tabindex', String(options.tabindex));
    }
    
    if (options.ariaExpanded !== undefined) {
        setAriaExpanded(element, options.ariaExpanded);
    }
    
    if (options.ariaLabel) {
        setAriaLabel(element, options.ariaLabel);
    }
}

/**
 * Removes accessibility attributes from an element (useful for cleanup)
 * @param element - The HTML element to clean up
 * @param attributes - Array of attribute names to remove
 */
export function removeAriaAttributes(element: HTMLElement | null, attributes: string[]): void {
    if (!element) return;
    
    attributes.forEach(attr => {
        element.removeAttribute(attr);
    });
}

/**
 * Checks if a keyboard event is an activation key (Enter or Space)
 * @param event - The keyboard event to check
 * @returns true if the event is an activation key
 */
export function isActivationKey(event: KeyboardEvent): boolean {
    return event.key === 'Enter' || event.key === ' ';
}

/**
 * Announces a message to screen readers using a live region
 * @param message - The message to announce
 * @param priority - The announcement priority ('polite' or 'assertive')
 * @param timeout - How long to display the message (milliseconds)
 */
export function announceToScreenReader(
    message: string, 
    priority: 'polite' | 'assertive' = 'polite', 
    timeout: number = 1000
): void {
    const liveRegionId = `live-region-${priority}`;
    let liveRegion = document.getElementById(liveRegionId);
    
    if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = liveRegionId;
        liveRegion.className = 'sr-only';
        liveRegion.setAttribute('aria-live', priority);
        liveRegion.setAttribute('aria-atomic', 'true');
        document.body.appendChild(liveRegion);
    }
    
    liveRegion.textContent = message;
    
    if (timeout > 0) {
        setTimeout(() => {
            if (liveRegion) liveRegion.textContent = '';
        }, timeout);
    }
}

/**
* Hides or shows an element *and* removes/restores focusability
* for every interactive descendant (links, buttons, form fields, etc.).
* @param element - The HTML element representing a whole �panel� or �slide�
* @param hidden  - true = hide and remove from tab order, false = show
*/
export function setHiddenAndUnfocusable(
    element: HTMLElement | null,
    hidden: boolean
): void {
    if (!element) return;

    setAriaHidden(element, hidden);

    element
        .querySelectorAll<HTMLElement>(
            'a, button, input, select, textarea, [tabindex]'
        )
        .forEach(el => {
            el.tabIndex = hidden ? -1 : 0;
        });
}

/**
* Sets or removes the inert attribute on an element
* (keeps all descendants out of the focus order in modern browsers)
* @param element - The HTML element to update
* @param inert   - true to add inert, false to remove, null to leave unchanged
*/
export function setInert(element: HTMLElement | null, inert: boolean | null): void {
    if (!element) return;

    if (inert === null) return;
    if (inert) {
        element.setAttribute('inert', '');
    } else {
        element.removeAttribute('inert');
    }
}
